import React, { useState, useEffect, useCallback } from "react";

const SUPABASE_URL = "https://ikgtmkoyncqgstobmhnj.supabase.co";
const SUPABASE_KEY = "sb_publishable_nmjm_t9li9oy47dN951wsQ_iNwDQAWf";
const ADMIN_EMAIL = "bri@olewalekilaviri.com";

// Auth helpers
const auth = {
  async signUp(email, password) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message || data.msg || JSON.stringify(data));
    return data;
  },
  async signIn(email, password) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (data.error || data.error_description) throw new Error(data.error_description || data.error);
    return data;
  },
  async signOut(token) {
    await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` },
    });
  },
  getSession() {
    try { return JSON.parse(localStorage.getItem("pjc_session") || "null"); } catch { return null; }
  },
  saveSession(s) { localStorage.setItem("pjc_session", JSON.stringify(s)); },
  clearSession() { localStorage.removeItem("pjc_session"); },
};

// DB helpers
const db = {
  async query(table, options = {}, token = null) {
    let url = `${SUPABASE_URL}/rest/v1/${table}?`;
    if (options.select) url += `select=${options.select}&`;
    if (options.filter) url += `${options.filter}&`;
    if (options.order) url += `order=${options.order}&`;
    const headers = { apikey: SUPABASE_KEY, "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  async insert(table, data, token = null) {
    const headers = { apikey: SUPABASE_KEY, "Content-Type": "application/json", Prefer: "return=representation" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
      method: "POST", headers, body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  async upsert(table, data, token = null, onConflict = "user_id") {
    const headers = {
      apikey: SUPABASE_KEY,
      "Content-Type": "application/json",
      Prefer: "return=representation,resolution=merge-duplicates",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const url = `${SUPABASE_URL}/rest/v1/${table}?on_conflict=${onConflict}`;
    const res = await fetch(url, {
      method: "POST", headers, body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  async update(table, filter, data, token = null) {
    const headers = { apikey: SUPABASE_KEY, "Content-Type": "application/json", Prefer: "return=representation" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${filter}`, {
      method: "PATCH", headers, body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};

const PROVINCES = [
  "National Capital District","Central","Gulf","Milne Bay","Oro (Northern)",
  "Western","Southern Highlands","Hela","Enga","Western Highlands","Jiwaka",
  "Chimbu (Simbu)","Eastern Highlands","Morobe","Madang","East Sepik",
  "West Sepik (Sandaun)","Manus","New Ireland","East New Britain",
  "West New Britain","Bougainville (AROB)",
];
const JOB_TYPES = ["Full-time","Part-time","Casual","Contract","Project-based"];
const STATUS_FLOW = ["Submitted","Viewed","Shortlisted","Interview","Hired","Unsuccessful"];
const TIPS = [
  ["How to write a CV","Keep it to 2–3 pages. Put your name, phone, province and district at the top. List education from most recent. For each job, write what you actually did. Always include two referees with working phone numbers."],
  ["How to avoid job scams","Real employers never ask you to pay money to apply. If a job asks for payment, report it immediately. Check for the Verified Employer badge and a clear closing date."],
  ["How to prepare for an interview","Read the job ad again the night before. Prepare answers for 'Tell me about yourself' and 'Why do you want this job?'. Arrive 15 minutes early. Bring copies of your CV and certificates."],
  ["How to apply professionally","Follow the application instructions exactly. Write a short cover letter for every job, even casual work. Use one email address with your real name."],
  ["How to scan documents with your phone","Place the document on a flat dark surface in good light. Use a free scanner app or your camera's document mode, then save as PDF."],
];

const EMPTY_CV = {
  full_name:"", phone:"", email:"", province:"Central", district:"",
  objective:"", skills:"", languages:"English, Tok Pisin", licences:"",
  education:[{school:"",quals:"",years:""}],
  experience:[{role:"",org:"",years:"",duties:""}],
  referee1:"", referee2:"", availability:"Immediately", relocate:true,
};

export default function App() {
  const [session, setSession] = useState(auth.getSession());
  const [userRole, setUserRole] = useState("seeker"); // seeker | employer | admin
  const [authMode, setAuthMode] = useState("login"); // login | register
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [cvSaving, setCvSaving] = useState(false);
  const [cvSaved, setCvSaved] = useState(false);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvFileUrl, setCvFileUrl] = useState(null);
  const [cvFileName, setCvFileName] = useState(null);
  const [aiExtracting, setAiExtracting] = useState(false);

  const [q, setQ] = useState("");
  const [fProv, setFProv] = useState("");
  const [fType, setFType] = useState("");
  const [activeJob, setActiveJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [cover, setCover] = useState("");
  const [answers, setAnswers] = useState([]);

  const [cv, setCv] = useState(EMPTY_CV);
  const [activeApp, setActiveApp] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isApprovedEmployer, setIsApprovedEmployer] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResults, setCsvResults] = useState(null);

  const [newJob, setNewJob] = useState({
    title:"", province:"National Capital District", district:"", type:"Full-time",
    salary:"", closing:"", description:"", requirements:"", screening:"", positions:1,
    employer_name:"", employer_contact:"", employer_phone:"", employer_email:"", employer_industry:"",
  });

  const token = session?.access_token || null;
  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 3200); };

  // Load CV from DB when logged in
  useEffect(() => {
    if (session?.user?.id) {
      loadCV();
      checkEmployerStatus();
    } else {
      setCv(EMPTY_CV);
      setIsApprovedEmployer(false);
      // Reset to seeker view if not logged in
      if (userRole !== "seeker") { setUserRole("seeker"); setTab("jobs"); }
    }
  }, [session]);

  useEffect(() => {
    loadJobs();
    if (session) loadApplications();
    if (userRole === "admin") { loadAllJobs(); loadEmployers(); loadReports(); loadApplications(); }
    if (userRole === "employer") { loadAllJobs(); loadApplications(); }
  }, [userRole, session]);

  useEffect(() => {
    if (session?.user?.id && applications.length > 0 && jobs.length > 0) {
      checkNotifications(applications, jobs);
    }
  }, [session, applications.length, jobs.length]);

  const loadCV = async () => {
    if (!session?.user?.id) return;
    try {
      const data = await db.query("jobseekers", {
        select: "*",
        filter: `user_id=eq.${session.user.id}`,
      }, token);
      if (data && data.length > 0) {
        const row = data[0];
        // Restore uploaded file info
        if (row.cv_file_url) {
          setCvFileUrl(row.cv_file_url);
          setCvFileName(row.cv_file_name || "My CV");
        }
        // Restore all CV fields
        const cvData = row.cv_data || {};
        setCv({
          full_name: row.full_name || "",
          phone: row.phone || "",
          email: row.email || session.user.email || "",
          province: row.province || "Central",
          district: row.district || "",
          objective: row.objective || "",
          skills: row.skills || "",
          languages: row.languages || "English, Tok Pisin",
          licences: row.licences || "",
          education: (cvData.education && cvData.education.length > 0 && cvData.education[0].school)
            ? cvData.education
            : [{school:"",quals:"",years:""}],
          experience: (cvData.experience && cvData.experience.length > 0 && cvData.experience[0].role)
            ? cvData.experience
            : [{role:"",org:"",years:"",duties:""}],
          referee1: cvData.referee1 || "",
          referee2: cvData.referee2 || "",
          availability: row.availability || "Immediately",
          relocate: row.willing_to_relocate !== false,
        });
        console.log("CV loaded:", row.full_name, "education:", cvData.education?.length, "exp:", cvData.experience?.length);
      } else {
        setCv({ ...EMPTY_CV, email: session.user.email || "" });
        console.log("No CV found in database for user");
      }
    } catch (e) {
      console.error("CV load error:", e);
    }
  };

  // Upload CV file to Supabase Storage
  const uploadCVFile = async (file) => {
    if (!session?.user?.id) { setShowAuth(true); return; }
    if (!file) return;
    const allowed = ["application/pdf","application/msword","application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) { flash("Please upload a PDF or Word document (.pdf, .doc, .docx)"); return; }
    if (file.size > 5 * 1024 * 1024) { flash("File must be under 5MB"); return; }
    setCvUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${session.user.id}/cv_${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/CV/${path}`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": file.type,
          "x-upsert": "true",
        },
        body: file,
      });
      if (!res.ok) throw new Error(await res.text());
      const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/CV/${path}`;
      setCvFileUrl(publicUrl);
      setCvFileName(file.name);
      // Save URL to jobseekers table
      await db.upsert("jobseekers", {
        user_id: session.user.id,
        email: session.user.email,
        cv_file_url: publicUrl,
        cv_file_name: file.name,
      }, token);
      flash("CV file uploaded ✓ — now extracting your details with AI...");
      // Trigger AI extraction
      await extractCVWithAI(file);
    } catch(e) {
      flash("Upload error: " + e.message);
    } finally {
      setCvUploading(false);
    }
  };

  // Use Claude AI to extract CV details from uploaded file
  const extractCVWithAI = async (file) => {
    setAiExtracting(true);
    try {
      // Only PDF is supported for AI extraction
      const isPDF = file.type === "application/pdf";
      if (!isPDF) {
        flash("AI extraction works best with PDF files. Please fill in your details manually or convert your CV to PDF.");
        setAiExtracting(false);
        return;
      }
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch(`${SUPABASE_URL}/functions/v1/super-service`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_KEY,
        },
        body: JSON.stringify({ base64, mediaType: "application/pdf" }),
      });
      if (!res.ok) {
        throw new Error(`Edge function error: ${res.status}`);
      }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const textBlock = data.content?.find(b => b.type === "text");
      if (!textBlock) throw new Error("No text in response");
      const clean = textBlock.text.replace(/```json|```/g, "").trim();
      const extracted = JSON.parse(clean);
      // Update CV state with extracted data
      const updatedCv = {
        full_name: extracted.full_name || "",
        phone: extracted.phone || "",
        email: extracted.email || session?.user?.email || "",
        province: extracted.province || "Central",
        district: extracted.district || "",
        objective: extracted.objective || "",
        skills: extracted.skills || "",
        languages: extracted.languages || "English, Tok Pisin",
        licences: extracted.licences || "",
        availability: extracted.availability || "Immediately",
        relocate: true,
        education: (extracted.education && extracted.education.length > 0 && extracted.education[0].school)
          ? extracted.education
          : [{school:"",quals:"",years:""}],
        experience: (extracted.experience && extracted.experience.length > 0 && extracted.experience[0].role)
          ? extracted.experience
          : [{role:"",org:"",years:"",duties:""}],
        referee1: extracted.referee1 || "",
        referee2: extracted.referee2 || "",
      };
      setCv(updatedCv);
      // Auto-save to database immediately after extraction
      try {
        await db.upsert("jobseekers", {
          user_id: session.user.id,
          full_name: updatedCv.full_name,
          phone: updatedCv.phone,
          email: updatedCv.email,
          province: updatedCv.province,
          district: updatedCv.district,
          objective: updatedCv.objective,
          skills: updatedCv.skills,
          languages: updatedCv.languages,
          licences: updatedCv.licences,
          availability: updatedCv.availability,
          willing_to_relocate: updatedCv.relocate,
          cv_file_url: cvFileUrl || null,
          cv_file_name: cvFileName || null,
          cv_data: {
            education: updatedCv.education,
            experience: updatedCv.experience,
            referee1: updatedCv.referee1,
            referee2: updatedCv.referee2,
          },
        }, token);
        flash("✓ CV extracted and saved! Your profile has been updated automatically.");
      } catch(saveErr) {
        flash("✓ CV details extracted! Click Save CV to save your profile.");
        console.error("Auto-save error:", saveErr);
      }
    } catch(e) {
      flash("AI extraction had an issue — please fill in the fields manually. Your file is still saved.");
      console.error("AI extraction error:", e.message);
    } finally {
      setAiExtracting(false);
    }
  };

  const saveCV = async () => {
    if (!session?.user?.id) { setShowAuth(true); return; }
    setCvSaving(true);
    try {
      await db.upsert("jobseekers", {
        user_id: session.user.id,
        full_name: cv.full_name,
        phone: cv.phone,
        email: cv.email || session.user.email,
        province: cv.province,
        district: cv.district,
        objective: cv.objective,
        skills: cv.skills,
        languages: cv.languages,
        licences: cv.licences,
        availability: cv.availability,
        willing_to_relocate: cv.relocate,
        cv_file_url: cvFileUrl || null,
        cv_file_name: cvFileName || null,
        cv_data: {
          education: cv.education,
          experience: cv.experience,
          referee1: cv.referee1,
          referee2: cv.referee2,
        },
      }, token);
      setCvSaved(true);
      flash("CV saved successfully ✓");
      setTimeout(() => setCvSaved(false), 3000);
    } catch (e) {
      flash("Error saving CV: " + e.message);
    } finally {
      setCvSaving(false);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await db.query("jobs", { select:"*", filter:"status=eq.live", order:"created_at.desc" });
      setJobs(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadAllJobs = async () => {
    try {
      const data = await db.query("jobs", { select:"*", order:"created_at.desc" }, token);
      setJobs(data);
    } catch (e) { console.error(e); }
  };

  const loadEmployers = async () => {
    try {
      const data = await db.query("employers", { select:"*", order:"created_at.desc" }, token);
      setEmployers(data);
    } catch (e) { console.error(e); }
  };

  const loadApplications = async () => {
    try {
      const data = await db.query("applications", { select:"*", order:"created_at.desc" }, token);
      setApplications(data);
    } catch (e) { console.error(e); }
  };

  const loadReports = async () => {
    try {
      const data = await db.query("scam_reports", { select:"*", order:"created_at.desc" }, token);
      setReports(data);
    } catch (e) { console.error(e); }
  };

  const checkEmployerStatus = async () => {
    if (!session?.user?.email) return;
    try {
      const data = await db.query("employers", {
        select: "id,status",
        filter: `email=eq.${session.user.email}&status=eq.approved`,
      }, token);
      setIsApprovedEmployer(data && data.length > 0);
    } catch (e) { console.error(e); }
  };

  // Auth
  const handleAuth = async () => {
    if (!authEmail || !authPassword) { setAuthError("Please enter your email and password."); return; }
    if (authPassword.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      let result;
      if (authMode === "register") {
        result = await auth.signUp(authEmail, authPassword);
        if (result.user || result.id || result.access_token) {
          // Try to sign in immediately after register
          try {
            result = await auth.signIn(authEmail, authPassword);
          } catch {}
        }
      } else {
        result = await auth.signIn(authEmail, authPassword);
      }
      if (result.access_token) {
        auth.saveSession(result);
        setSession(result);
        setShowAuth(false);
        flash(authMode === "register" ? "Account created — welcome!" : "Welcome back!");
        setAuthEmail(""); setAuthPassword("");
      } else if (authMode === "register" && (result.user || result.id)) {
        // Account created but email confirmation required
        setAuthError("");
        setShowAuth(false);
        flash("Account created! Check your email to confirm, then sign in.");
        setAuthMode("login");
      } else {
        setAuthError("Could not sign in. Please check your email and password.");
      }
    } catch (e) {
      setAuthError(e.message || "Authentication failed. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (token) await auth.signOut(token).catch(() => {});
    auth.clearSession();
    setSession(null);
    setCv(EMPTY_CV);
    flash("Signed out.");
  };

  const cvScore = (() => {
    let s = 0;
    [cv.full_name, cv.phone, cv.province, cv.objective, cv.skills,
     cv.education[0]?.school, cv.experience[0]?.role, cv.referee1]
      .forEach(c => c && c.trim() && (s += 12.5));
    return Math.round(s);
  })();

  const myApps = applications.filter(a => a.user_id === session?.user?.id);
  const hasApplied = (jobId) => myApps.some(a => a.job_id === jobId);

  const filteredJobs = jobs.filter(j => {
    const hay = (j.title + " " + j.description + " " + j.province).toLowerCase();
    return (!q || hay.includes(q.toLowerCase())) &&
      (!fProv || j.province === fProv) &&
      (!fType || j.job_type === fType);
  });

  const startApply = (job) => {
    if (!session) { setShowAuth(true); return; }
    if (cvScore < 50) { flash("Complete your CV first — at least 50%."); setTab("cv"); return; }
    setApplying(true);
    setAnswers((job.screening_questions || []).map(() => ""));
    setCover("");
  };

  const genCover = (job) => {
    const empName = job.employer_name || "the employer";
    const skills = cv.skills || "relevant practical skills";
    const exp = cv.experience[0]?.role
      ? `In my time as ${cv.experience[0].role}${cv.experience[0].org ? " at " + cv.experience[0].org : ""}, I built strong reliable work habits. ` : "";
    setCover(
`Dear Hiring Manager at ${empName},

I am writing to apply for the position of ${job.title} advertised on PNG Jobs Connect. I am based in ${cv.district ? cv.district + ", " : ""}${cv.province} and available to start ${cv.availability?.toLowerCase()}.

${exp}My key skills include ${skills}. I meet the requirements listed and have attached my CV and supporting documents.

Thank you for considering my application. I am happy to attend an interview and can be reached on ${cv.phone || "my listed phone number"}.

Yours faithfully,
${cv.full_name}`);
  };

  const submitApp = async (job) => {
    if (!session) { setShowAuth(true); return; }
    if ((job.screening_questions || []).length > 0 && answers.some(a => !a)) {
      flash("Please answer all screening questions."); return;
    }
    setLoading(true);
    try {
      await db.insert("applications", {
        job_id: job.id,
        user_id: session.user.id,
        applicant_name: cv.full_name || session.user.email,
        cover_letter: cover,
        screening_answers: answers,
        status: "Submitted",
        cv_file_url: cvFileUrl || null,
        cv_file_name: cvFileName || null,
      }, token);
      await loadApplications();
      setApplying(false); setActiveJob(null);
      flash("Application submitted ✓ — track it in My Applications.");
    } catch (e) { flash("Error: " + e.message); }
    finally { setLoading(false); }
  };

  const submitJob = async () => {
    if (!newJob.title || !newJob.description || !newJob.closing || !newJob.employer_name) {
      flash("Company name, title, description and closing date are required."); return;
    }
    setLoading(true);
    try {
      let empId = null;
      const existing = employers.find(e => e.name === newJob.employer_name);
      if (existing) {
        empId = existing.id;
      } else {
        const [emp] = await db.insert("employers", {
          name: newJob.employer_name,
          contact_person: newJob.employer_contact,
          phone: newJob.employer_phone,
          email: newJob.employer_email,
          industry: newJob.employer_industry,
          province: newJob.province,
          verification_level: "Unverified",
          status: "pending",
        }, token);
        empId = emp?.id;
        await loadEmployers();
      }
      await db.insert("jobs", {
        employer_id: empId,
        employer_name: newJob.employer_name,
        title: newJob.title,
        province: newJob.province,
        district: newJob.district,
        job_type: newJob.type,
        salary: newJob.salary,
        description: newJob.description,
        requirements: newJob.requirements.split("\n").filter(Boolean),
        screening_questions: newJob.screening.split("\n").filter(Boolean),
        docs_required: ["CV"],
        closing_date: newJob.closing,
        positions: newJob.positions,
        status: "pending",
        posted_by: session?.user?.id || null,
      }, token);
      setNewJob({ title:"", province:"National Capital District", district:"", type:"Full-time", salary:"", closing:"", description:"", requirements:"", screening:"", positions:1, employer_name:"", employer_contact:"", employer_phone:"", employer_email:"", employer_industry:"" });
      await loadAllJobs();
      flash("Job submitted — awaiting admin approval.");
      setTab("dash");
    } catch (e) { flash("Error: " + e.message); }
    finally { setLoading(false); }
  };

  const sendEmailNotification = async (appId, status, currentApps, currentJobs) => {
    try {
      const a = (currentApps || applications).find(x => x.id === appId);
      if (!a) return;
      const j = (currentJobs || jobs).find(x => x.id === a.job_id) || {};
      const seekers = await db.query("jobseekers", { select:"email,full_name", filter:`user_id=eq.${a.user_id}` }, token);
      const seeker = seekers?.[0];
      if (!seeker?.email) return;
      await fetch(`${SUPABASE_URL}/functions/v1/notify-applicant`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ applicant_email: seeker.email, applicant_name: seeker.full_name || a.applicant_name, job_title: j.title || "the position", employer_name: j.employer_name || "the employer", status }),
      });
    } catch (e) { console.error("Email error:", e); }
  };

  const checkNotifications = async (currentApps, currentJobs) => {
    if (!session?.user?.id) return;
    const appsToCheck = currentApps || applications;
    const jobsToCheck = currentJobs || jobs;
    const notified = JSON.parse(localStorage.getItem("pjc_notified") || "{}");
    const newNotifs = [];
    for (const a of appsToCheck.filter(x => x.user_id === session.user.id)) {
      const key = `${a.id}_${a.status}`;
      if (!notified[key] && ["Shortlisted","Interview","Hired","Unsuccessful"].includes(a.status)) {
        const j = jobsToCheck.find(x => x.id === a.job_id) || {};
        const msgs = {
          Shortlisted: `🎉 Great news! Your application for ${j.title || "a job"} at ${j.employer_name || "an employer"} has been shortlisted.`,
          Interview: `📅 Interview request! ${j.employer_name || "An employer"} wants to interview you for ${j.title || "a position"}.`,
          Hired: `🏆 Congratulations! You have been selected for ${j.title || "the position"} at ${j.employer_name || "the employer"}.`,
          Unsuccessful: `Your application for ${j.title || "a position"} was not successful this time. Keep applying — new jobs are added every week.`,
        };
        newNotifs.push({ id: key, msg: msgs[a.status], status: a.status });
        notified[key] = true;
      }
    }
    if (newNotifs.length > 0) {
      localStorage.setItem("pjc_notified", JSON.stringify(notified));
      setNotifications(prev => [...newNotifs, ...prev]);
    }
  };

  const updateAppStatus = async (id, status) => {
    try {
      await db.update("applications", `id=eq.${id}`, { status }, token);
      const updated = await db.query("applications", { select:"*", order:"created_at.desc" }, token);
      setApplications(updated);
      if (["Shortlisted","Interview","Hired","Unsuccessful"].includes(status)) {
        sendEmailNotification(id, status, updated, jobs);
      }
    } catch (e) { flash("Error: " + e.message); }
  };

  const approveJob = async (id, approve) => {
    try {
      await db.update("jobs", `id=eq.${id}`, { status: approve ? "live" : "rejected" }, token);
      await loadAllJobs();
      flash(approve ? "Job approved and live." : "Job rejected.");
    } catch (e) { flash("Error: " + e.message); }
  };

  const approveEmp = async (id, approve) => {
    try {
      await db.update("employers", `id=eq.${id}`, { status: approve ? "approved" : "rejected", verification_level: approve ? "Phone verified" : "Unverified" }, token);
      await loadEmployers();
      flash(approve ? "Employer verified." : "Rejected.");
    } catch (e) { flash("Error: " + e.message); }
  };

  const resolveReport = async (id) => {
    try {
      await db.update("scam_reports", `id=eq.${id}`, { status: "resolved" }, token);
      await loadReports();
      flash("Report resolved.");
    } catch (e) { flash("Error: " + e.message); }
  };

  const reportJob = async (job) => {
    try {
      await db.insert("scam_reports", { job_id: job.id, reason: "Reported by user", reported_by: cv.full_name || session?.user?.email || "Anonymous", status: "open" }, token);
      flash("Report sent to admin. Thank you.");
    } catch (e) { flash("Error: " + e.message); }
  };

  const switchRole = (r) => {
    // Security check
    if (r === "admin" && session?.user?.email !== ADMIN_EMAIL) return;
    if (r === "employer" && !isApprovedEmployer && session?.user?.email !== ADMIN_EMAIL) return;
    setUserRole(r);
    setTab(r === "seeker" ? "jobs" : r === "employer" ? "dash" : "approvals");
    setActiveJob(null); setApplying(false);
    if (r === "employer") loadAllJobs();
    if (r === "admin") { loadAllJobs(); loadEmployers(); loadReports(); loadApplications(); }
  };

  const seekerTabs = [["jobs","Find Jobs"],["apps","My Applications"],["cv","My CV"],["learn","Career Tips"]];
  const empTabs = [["dash","My Jobs"],["post","Post a Job"]];
  const adminTabs = [["approvals","Approvals"],["reports","Scam Reports"],["stats","Stats"]];
  const tabsFor = userRole === "seeker" ? seekerTabs : userRole === "employer" ? empTabs : adminTabs;
  const pendingCount = jobs.filter(j=>j.status==="pending").length + employers.filter(e=>e.status==="pending").length;

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResults(null);
    try {
      const text = await file.text();
      const lines = text.trim().split("\n");
      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g,""));
      const rows = lines.slice(1);
      let success = 0, failed = 0, errors = [];
      for (const row of rows) {
        if (!row.trim()) continue;
        // Parse CSV properly handling quoted fields with commas
        const fields = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < row.length; i++) {
          const ch = row[i];
          if (ch === '"') { inQuotes = !inQuotes; }
          else if (ch === "," && !inQuotes) { fields.push(current.trim()); current = ""; }
          else { current += ch; }
        }
        fields.push(current.trim());
        const job = {};
        headers.forEach((h, i) => { job[h] = (fields[i] || "").replace(/^"|"$/g,""); });
        if (!job.title || !job.employer_name) continue;
        const reqs = job.requirements ? job.requirements.split(",").map(r => r.trim()) : [];
        const jobData = {
          title: job.title,
          employer_name: job.employer_name,
          province: job.province || "National Capital District",
          district: job.district || "",
          job_type: job.job_type || "Full-time",
          salary: job.salary || "",
          description: job.description || "",
          requirements: reqs,
          screening_questions: [],
          docs_required: ["CV"],
          closing_date: job.closing_date || null,
          positions: parseInt(job.positions) || 1,
          status: "pending",
        };
        try {
          await db.insert("jobs", jobData, token);
          success++;
        } catch(err) {
          failed++;
          errors.push(job.title + ": " + err.message);
        }
        await new Promise(r => setTimeout(r, 200));
      }
      await loadAllJobs();
      setCsvResults({ success, failed, errors });
    } catch(err) {
      setCsvResults({ success: 0, failed: 1, errors: [err.message] });
    } finally {
      setCsvUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="root">
      <style>{CSS}</style>

      {/* Header */}
      <header className="hdr">
        <div className="hdr-in">
          <div className="brand">
            <svg width="36" height="36" viewBox="0 0 36 36"><rect width="36" height="36" rx="8" fill="#171310"/><path d="M0 36L36 0v36z" fill="#C8102E"/><circle cx="26" cy="26" r="2.5" fill="#F0AC12"/><circle cx="10" cy="11" r="1.8" fill="#FBF8F2"/><circle cx="15" cy="7" r="1.4" fill="#FBF8F2"/><circle cx="7" cy="16" r="1.4" fill="#FBF8F2"/></svg>
            <div>
              <div className="logo-text">PNG Jobs <em>Connect</em></div>
              <div className="logo-tag">Wok i stap. Painim gutpela wok.</div>
            </div>
          </div>
          <div className="hdr-right">
            <div className="role-sw">
              <button className={"role-btn"+(userRole==="seeker"?" on":"")} onClick={()=>switchRole("seeker")}>Jobseeker</button>
              {(isApprovedEmployer || session?.user?.email === ADMIN_EMAIL) && (
                <button className={"role-btn"+(userRole==="employer"?" on":"")} onClick={()=>switchRole("employer")}>Employer</button>
              )}
              {session?.user?.email === ADMIN_EMAIL && (
                <button className={"role-btn"+(userRole==="admin"?" on":"")} onClick={()=>switchRole("admin")}>Admin</button>
              )}
            </div>
            {session
              ? <div className="user-pill">
                  <span className="user-email">{session.user?.email}</span>
                  <button className="btn ghost sm" onClick={handleSignOut}>Sign out</button>
                </div>
              : <button className="btn gold sm" onClick={()=>{setShowAuth(true);setAuthMode("login");}}>Sign in</button>
            }
          </div>
        </div>
        <div className="weave"/>
      </header>

      {userRole==="seeker" && (
        <div className="scam-warn">⚠ Never pay money to apply for a job. Real employers do not charge fees. <strong>Report</strong> any job asking for payment.</div>
      )}

      {notifications.length > 0 && (
        <div className="notif-wrap">
          {notifications.map((n,i) => (
            <div key={n.id} className={"notif-bar notif-"+n.status.toLowerCase()}>
              <span>{n.msg}</span>
              <button className="notif-close" onClick={()=>setNotifications(prev=>prev.filter((_,j)=>j!==i))}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Auth modal */}
      {showAuth && (
        <div className="overlay" onClick={()=>setShowAuth(false)}>
          <div className="modal auth-modal" onClick={e=>e.stopPropagation()}>
            <button className="close-x" onClick={()=>setShowAuth(false)}>✕</button>
            <div className="auth-logo">PNG Jobs <em>Connect</em></div>
            <div className="auth-tabs">
              <button className={"auth-tab"+(authMode==="login"?" on":"")} onClick={()=>{setAuthMode("login");setAuthError("");}}>Sign in</button>
              <button className={"auth-tab"+(authMode==="register"?" on":"")} onClick={()=>{setAuthMode("register");setAuthError("");}}>Create account</button>
            </div>
            <div className="sub-h">{authMode==="register"?"New jobseeker or employer account":"Welcome back"}</div>
            <label className="lbl">Email address</label>
            <input className="inp" type="email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} placeholder="your@email.com" onKeyDown={e=>e.key==="Enter"&&handleAuth()}/>
            <label className="lbl">Password {authMode==="register"&&<span className="muted">(min 6 characters)</span>}</label>
            <input className="inp" type="password" value={authPassword} onChange={e=>setAuthPassword(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&handleAuth()}/>
            {authError && <div className="auth-err">{authError}</div>}
            <button className="btn gold big" style={{marginTop:16,width:"100%"}} onClick={handleAuth} disabled={authLoading}>
              {authLoading ? "Please wait…" : authMode==="register" ? "Create account" : "Sign in"}
            </button>
            <p className="muted small" style={{marginTop:12,textAlign:"center"}}>
              {authMode==="register"
                ? <>Already have an account? <button className="link-btn" onClick={()=>setAuthMode("login")}>Sign in</button></>
                : <>No account yet? <button className="link-btn" onClick={()=>setAuthMode("register")}>Create one free</button></>}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <nav className="tabs">
        <div className="tabs-inner">
          {tabsFor.map(([id,label])=>(
            <button key={id} className={"tab-btn"+(tab===id?" on":"")} onClick={()=>{setTab(id);setActiveJob(null);}}>
              {label}
              {id==="apps" && myApps.length>0 && <span className="badge">{myApps.length}</span>}
              {id==="approvals" && pendingCount>0 && <span className="badge">{pendingCount}</span>}
            </button>
          ))}
        </div>
      </nav>

      <main className="main">

        {/* JOBSEEKER: Find Jobs */}
        {userRole==="seeker" && tab==="jobs" && (
          <>
            <div className="hero">
              <h1>Find real jobs across <span>Papua New Guinea</span></h1>
              <p>{jobs.length} verified {jobs.length===1?"vacancy":"vacancies"} live now.</p>
            </div>
            <div className="filter-row">
              <input className="f-inp grow" placeholder="Search: driver, security, admin…" value={q} onChange={e=>setQ(e.target.value)}/>
              <select className="f-inp" value={fProv} onChange={e=>setFProv(e.target.value)}>
                <option value="">All provinces</option>
                {PROVINCES.map(p=><option key={p}>{p}</option>)}
              </select>
              <select className="f-inp" value={fType} onChange={e=>setFType(e.target.value)}>
                <option value="">All types</option>
                {JOB_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            {loading && <div className="loading">Loading jobs…</div>}
            {!loading && filteredJobs.length===0 && (
              <div className="empty">No jobs found. Try different search terms or check back soon — new jobs are posted every week.</div>
            )}
            <div className="job-grid">
              {filteredJobs.map(j=>(
                <div key={j.id} className="jcard" onClick={()=>setActiveJob(j)}>
                  <div className="j-title">{j.title}</div>
                  <div className="j-emp">{j.employer_name}</div>
                  <div className="j-meta">
                    <Chip c="loc">{j.district?j.district+", ":""}{j.province}</Chip>
                    <Chip c="typ">{j.job_type}</Chip>
                    {j.salary&&<Chip c="sal">{j.salary}</Chip>}
                  </div>
                  <div className="j-foot">
                    <span>Closes <strong>{j.closing_date}</strong> · {j.positions} position{j.positions>1?"s":""}</span>
                    {hasApplied(j.id) ? <span className="applied-lbl">✓ Applied</span> : <span className="view-lnk">View & apply →</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* JOBSEEKER: My Applications */}
        {userRole==="seeker" && tab==="apps" && (
          <section>
            <h2 className="sec-h">My Applications</h2>
            {!session && <div className="auth-prompt">
              <p>Sign in to see your applications.</p>
              <button className="btn gold" onClick={()=>setShowAuth(true)}>Sign in</button>
            </div>}
            {session && myApps.length===0 && <div className="empty">No applications yet. Find a job and apply!</div>}
            {session && myApps.map(a=>{
              const j=jobs.find(x=>x.id===a.job_id)||{};
              return (
                <div key={a.id} className="row-card">
                  <div>
                    <div className="j-title sm">{j.title||"Job"}</div>
                    <div className="muted">{j.employer_name||""} · {a.created_at?.slice(0,10)}</div>
                  </div>
                  <StatusChip s={a.status}/>
                </div>
              );
            })}
          </section>
        )}

        {/* JOBSEEKER: My CV */}
        {userRole==="seeker" && tab==="cv" && (
          <div className="cv-wrap">
            <div className="cv-form">
              <div className="cv-head">
                <div>
                  <h2 className="sec-h" style={{marginBottom:2}}>My CV</h2>
                  {!session && <p className="muted small">Sign in to save your CV permanently to your account.</p>}
                </div>

              {/* CV Upload Box */}
              <div className="cv-upload-card">
                <div className="cv-upload-title">📄 Upload your existing CV</div>
                <p className="muted" style={{fontSize:13,marginBottom:10}}>Have a Word or PDF CV already? Upload it and AI will fill in your profile automatically. Your file is saved and sent to employers with every application.</p>
                {cvFileUrl && (
                  <div className="cv-file-saved">
                    <span>📎 {cvFileName || "CV uploaded"}</span>
                    <a href={cvFileUrl} target="_blank" rel="noopener noreferrer" className="btn ghost sm">Download</a>
                  </div>
                )}
                <label className={"btn"+(cvUploading||aiExtracting?" ghost":"")+" sm"} style={{marginTop:8,display:"inline-block",cursor:"pointer"}}>
                  {cvUploading ? "Uploading…" : aiExtracting ? "✨ AI reading your CV…" : cvFileUrl ? "Replace CV file" : "⬆ Upload PDF or Word CV"}
                  <input type="file" accept=".pdf,.doc,.docx" style={{display:"none"}} onChange={e=>e.target.files[0]&&uploadCVFile(e.target.files[0])} disabled={cvUploading||aiExtracting}/>
                </label>
                {aiExtracting && <p className="muted small" style={{marginTop:6}}>Claude AI is reading your CV and filling in your profile — this takes about 10 seconds…</p>}
              </div>
                <div className="cv-head-right">
                  <div className="score-bar-wrap">
                    <div className="score-bar"><div style={{width:cvScore+"%"}}/></div>
                    <span className="muted">{cvScore}%</span>
                  </div>
                  {session
                    ? <button className={"btn"+(cvSaved?" gold":"")} onClick={saveCV} disabled={cvSaving}>
                        {cvSaving?"Saving…":cvSaved?"✓ Saved":"Save CV"}
                      </button>
                    : <button className="btn gold" onClick={()=>setShowAuth(true)}>Sign in to save</button>
                  }
                </div>
              </div>

              <Label>Full name *</Label>
              <input className="inp" value={cv.full_name} onChange={e=>setCv({...cv,full_name:e.target.value})} placeholder="e.g. Mary Toua"/>
              <div className="row2">
                <div><Label>Phone</Label><input className="inp" value={cv.phone} onChange={e=>setCv({...cv,phone:e.target.value})} placeholder="7XXX XXXX"/></div>
                <div><Label>Email</Label><input className="inp" value={cv.email} onChange={e=>setCv({...cv,email:e.target.value})}/></div>
              </div>
              <div className="row2">
                <div><Label>Province</Label>
                  <select className="inp" value={cv.province} onChange={e=>setCv({...cv,province:e.target.value})}>{PROVINCES.map(p=><option key={p}>{p}</option>)}</select>
                </div>
                <div><Label>District / village</Label><input className="inp" value={cv.district} onChange={e=>setCv({...cv,district:e.target.value})} placeholder="e.g. Rigo — Hula"/></div>
              </div>
              <Label>Career objective</Label>
              <textarea className="inp" rows={2} value={cv.objective} onChange={e=>setCv({...cv,objective:e.target.value})} placeholder="One or two sentences about the work you want and what you offer."/>
              <Label>Skills (comma separated)</Label>
              <input className="inp" value={cv.skills} onChange={e=>setCv({...cv,skills:e.target.value})} placeholder="Driving, MS Word, customer service…"/>
              <div className="row2">
                <div><Label>Languages</Label><input className="inp" value={cv.languages} onChange={e=>setCv({...cv,languages:e.target.value})}/></div>
                <div><Label>Licences / certificates</Label><input className="inp" value={cv.licences} onChange={e=>setCv({...cv,licences:e.target.value})} placeholder="Class 3, First Aid…"/></div>
              </div>
              <div className="sub-h">Education</div>
              {cv.education.map((ed,i)=>(
                <div className="row3" key={i}>
                  <input className="inp" placeholder="School / institution" value={ed.school} onChange={e=>{const c=[...cv.education];c[i]={...ed,school:e.target.value};setCv({...cv,education:c});}}/>
                  <input className="inp" placeholder="Qualification" value={ed.quals} onChange={e=>{const c=[...cv.education];c[i]={...ed,quals:e.target.value};setCv({...cv,education:c});}}/>
                  <input className="inp" placeholder="Years" value={ed.years} onChange={e=>{const c=[...cv.education];c[i]={...ed,years:e.target.value};setCv({...cv,education:c});}}/>
                </div>
              ))}
              <button className="add-btn" onClick={()=>setCv({...cv,education:[...cv.education,{school:"",quals:"",years:""}]})}>+ Add education</button>
              <div className="sub-h">Work experience</div>
              {cv.experience.map((ex,i)=>(
                <div key={i} className="exp-block">
                  <div className="row3">
                    <input className="inp" placeholder="Role" value={ex.role} onChange={e=>{const c=[...cv.experience];c[i]={...ex,role:e.target.value};setCv({...cv,experience:c});}}/>
                    <input className="inp" placeholder="Organisation" value={ex.org} onChange={e=>{const c=[...cv.experience];c[i]={...ex,org:e.target.value};setCv({...cv,experience:c});}}/>
                    <input className="inp" placeholder="Years" value={ex.years} onChange={e=>{const c=[...cv.experience];c[i]={...ex,years:e.target.value};setCv({...cv,experience:c});}}/>
                  </div>
                  <input className="inp" placeholder="Main duties" value={ex.duties} onChange={e=>{const c=[...cv.experience];c[i]={...ex,duties:e.target.value};setCv({...cv,experience:c});}}/>
                </div>
              ))}
              <button className="add-btn" onClick={()=>setCv({...cv,experience:[...cv.experience,{role:"",org:"",years:"",duties:""}]})}>+ Add experience</button>
              <div className="sub-h">Referees</div>
              <div className="row2">
                <input className="inp" placeholder="Referee 1 — name, role, phone" value={cv.referee1} onChange={e=>setCv({...cv,referee1:e.target.value})}/>
                <input className="inp" placeholder="Referee 2 — name, role, phone" value={cv.referee2} onChange={e=>setCv({...cv,referee2:e.target.value})}/>
              </div>
              <div className="row2" style={{marginTop:8}}>
                <div><Label>Availability</Label>
                  <select className="inp" value={cv.availability} onChange={e=>setCv({...cv,availability:e.target.value})}>
                    <option>Immediately</option><option>Within 2 weeks</option><option>Within a month</option>
                  </select>
                </div>
                <div><Label>Willing to relocate?</Label>
                  <select className="inp" value={cv.relocate?"Yes":"No"} onChange={e=>setCv({...cv,relocate:e.target.value==="Yes"})}>
                    <option>Yes</option><option>No</option>
                  </select>
                </div>
              </div>
              <div className="save-row">
                {session
                  ? <button className={"btn big"+(cvSaved?" gold":"")} onClick={saveCV} disabled={cvSaving}>
                      {cvSaving?"Saving…":cvSaved?"✓ CV Saved":"Save CV"}
                    </button>
                  : <button className="btn gold big" onClick={()=>setShowAuth(true)}>Sign in to save your CV</button>
                }
                <button className="btn ghost big" onClick={()=>window.print()}>Print / Save as PDF</button>
              </div>
            </div>

            {/* CV Preview */}
            <div className="cv-preview">
              <div className="cv-paper">
                <div className="cv-name">{cv.full_name||"Your Name"}</div>
                <div className="cv-contact">{[cv.phone,cv.email,(cv.district?cv.district+", ":"")+cv.province].filter(Boolean).join("  ·  ")}</div>
                {cv.objective&&<><div className="cv-sec">Career Objective</div><p>{cv.objective}</p></>}
                {cv.skills&&<><div className="cv-sec">Skills</div><p>{cv.skills}</p></>}
                {cv.education[0]?.school&&<><div className="cv-sec">Education</div>{cv.education.filter(e=>e.school).map((e,i)=><p key={i}><strong>{e.quals||"—"}</strong>, {e.school}{e.years&&` (${e.years})`}</p>)}</>}
                {cv.experience[0]?.role&&<><div className="cv-sec">Work Experience</div>{cv.experience.filter(e=>e.role).map((e,i)=><p key={i}><strong>{e.role}</strong>, {e.org}{e.years&&` (${e.years})`}{e.duties&&<><br/><span className="muted">{e.duties}</span></>}</p>)}</>}
                {(cv.languages||cv.licences)&&<><div className="cv-sec">Languages & Licences</div><p>{[cv.languages,cv.licences].filter(Boolean).join("  ·  ")}</p></>}
                {(cv.referee1||cv.referee2)&&<><div className="cv-sec">Referees</div>{[cv.referee1,cv.referee2].filter(Boolean).map((r,i)=><p key={i}>{r}</p>)}</>}
                <p className="muted small">Availability: {cv.availability} · Relocate: {cv.relocate?"Yes":"No"}</p>
              </div>
            </div>
          </div>
        )}

        {/* Career Tips */}
        {userRole==="seeker" && tab==="learn" && (
          <section>
            <h2 className="sec-h">Career Learning Centre</h2>
            {TIPS.map(([t,b],i)=><Accordion key={i} title={t} body={b}/>)}
          </section>
        )}

        {/* EMPLOYER: My Jobs */}
        {userRole==="employer" && tab==="dash" && (
          <section>
            <h2 className="sec-h">My Posted Jobs</h2>
            {jobs.length===0&&!loading&&<div className="empty">No jobs posted yet. Click Post a Job to get started.</div>}
            {jobs.map(j=>{
              const japps=applications.filter(a=>a.job_id===j.id);
              return (
                <div key={j.id} className="jcard flat">
                  <div className="j-title">{j.title} <StatusChip s={j.status}/></div>
                  <div className="muted">{j.district?j.district+", ":""}{j.province} · closes {j.closing_date} · {japps.length} applicant{japps.length!==1?"s":""}</div>
                  {japps.length>0&&(
                    <div className="app-list">
                      {japps.map(a=>(
                        <div key={a.id} className="app-row">
                          <div className="app-row-name" onClick={()=>setActiveApp(a)} style={{cursor:"pointer",flex:1}}>
                            <div className="j-title sm" style={{color:"var(--red)"}}>{a.applicant_name} <span className="view-lnk" style={{fontSize:12}}>View application →</span></div>
                            <div className="muted small">{a.created_at?.slice(0,10)}</div>
                          </div>
                          <div className="pipeline">
                            <StatusChip s={a.status}/>
                            <select className="inp tiny" value={a.status} onChange={e=>updateAppStatus(a.id,e.target.value)}>
                              {STATUS_FLOW.map(s=><option key={s}>{s}</option>)}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* EMPLOYER: Post a Job */}
        {userRole==="employer" && tab==="post" && (
          <section className="post-wrap">
            <h2 className="sec-h">Post a Job</h2>
            <p className="muted note">All jobs are reviewed by admin before going live — this prevents fake ads.</p>
            <div className="sub-h">Company details</div>
            <div className="row2">
              <div><Label>Company name *</Label><input className="inp" value={newJob.employer_name} onChange={e=>setNewJob({...newJob,employer_name:e.target.value})} placeholder="e.g. Pacifik Security Services"/></div>
              <div><Label>Industry</Label><input className="inp" value={newJob.employer_industry} onChange={e=>setNewJob({...newJob,employer_industry:e.target.value})} placeholder="e.g. Security, ICT"/></div>
            </div>
            <div className="row2">
              <div><Label>Contact person</Label><input className="inp" value={newJob.employer_contact} onChange={e=>setNewJob({...newJob,employer_contact:e.target.value})}/></div>
              <div><Label>Phone</Label><input className="inp" value={newJob.employer_phone} onChange={e=>setNewJob({...newJob,employer_phone:e.target.value})} placeholder="7XXX XXXX"/></div>
            </div>
            <div className="sub-h">Job details</div>
            <Label>Job title *</Label>
            <input className="inp" value={newJob.title} onChange={e=>setNewJob({...newJob,title:e.target.value})} placeholder="e.g. Office Administrator"/>
            <div className="row3">
              <div><Label>Province</Label>
                <select className="inp" value={newJob.province} onChange={e=>setNewJob({...newJob,province:e.target.value})}>{PROVINCES.map(p=><option key={p}>{p}</option>)}</select>
              </div>
              <div><Label>District</Label><input className="inp" value={newJob.district} onChange={e=>setNewJob({...newJob,district:e.target.value})}/></div>
              <div><Label>Job type</Label>
                <select className="inp" value={newJob.type} onChange={e=>setNewJob({...newJob,type:e.target.value})}>{JOB_TYPES.map(t=><option key={t}>{t}</option>)}</select>
              </div>
            </div>
            <div className="row3">
              <div><Label>Salary (optional)</Label><input className="inp" value={newJob.salary} onChange={e=>setNewJob({...newJob,salary:e.target.value})} placeholder="e.g. K800–K1,000 / fortnight"/></div>
              <div><Label>Closing date *</Label><input className="inp" type="date" value={newJob.closing} onChange={e=>setNewJob({...newJob,closing:e.target.value})}/></div>
              <div><Label>Positions</Label><input className="inp" type="number" min={1} value={newJob.positions} onChange={e=>setNewJob({...newJob,positions:+e.target.value})}/></div>
            </div>
            <Label>Job description *</Label>
            <textarea className="inp" rows={4} value={newJob.description} onChange={e=>setNewJob({...newJob,description:e.target.value})}/>
            <Label>Requirements (one per line)</Label>
            <textarea className="inp" rows={3} value={newJob.requirements} onChange={e=>setNewJob({...newJob,requirements:e.target.value})} placeholder={"Grade 12 certificate\n2 years experience"}/>
            <Label>Screening questions (one per line, optional)</Label>
            <textarea className="inp" rows={2} value={newJob.screening} onChange={e=>setNewJob({...newJob,screening:e.target.value})} placeholder={"Do you have a driver's licence?"}/>
            <div className="save-row">
              <button className="btn gold big" onClick={submitJob} disabled={loading}>{loading?"Submitting…":"Submit for approval"}</button>
            </div>
          </section>
        )}

        {/* ADMIN */}
        {userRole==="admin" && tab==="approvals" && (
          <section>
            <h2 className="sec-h">Pending Employer Verifications</h2>
            {employers.filter(e=>e.status==="pending").length===0&&<div className="empty sm">No employers waiting.</div>}
            {employers.filter(e=>e.status==="pending").map(e=>(
              <div key={e.id} className="row-card">
                <div><div className="j-title sm">{e.name}</div><div className="muted">{e.industry} · {e.province} · {e.contact_person} · {e.phone}</div></div>
                <div className="btn-row">
                  <button className="btn gold" onClick={()=>approveEmp(e.id,true)}>Verify</button>
                  <button className="btn ghost" onClick={()=>approveEmp(e.id,false)}>Reject</button>
                </div>
              </div>
            ))}
            <h2 className="sec-h gap">Pending Job Posts</h2>
            {jobs.filter(j=>j.status==="pending").length===0&&<div className="empty sm">No jobs waiting.</div>}
            {jobs.filter(j=>j.status==="pending").map(j=>(
              <div key={j.id} className="row-card">
                <div>
                  <div className="j-title sm">{j.title}</div>
                  <div className="muted">{j.employer_name} · {j.district?j.district+", ":""}{j.province} · closes {j.closing_date}</div>
                  <div className="muted small">{(j.description||"").slice(0,120)}…</div>
                </div>
                <div className="btn-row">
                  <button className="btn gold" onClick={()=>approveJob(j.id,true)}>Approve</button>
                  <button className="btn ghost" onClick={()=>approveJob(j.id,false)}>Reject</button>
                </div>
              </div>
            ))}
          </section>
        )}

        {userRole==="admin" && tab==="reports" && (
          <section>
            <h2 className="sec-h">Scam & Abuse Reports</h2>
            {reports.length===0&&<div className="empty">No reports yet.</div>}
            {reports.map(r=>(
              <div key={r.id} className={"row-card"+(r.status==="resolved"?" dim":"")}>
                <div>
                  <div className="j-title sm">Report #{r.id?.slice(0,8)}</div>
                  <div className="muted">Reason: {r.reason} · by {r.reported_by} · {r.created_at?.slice(0,10)}</div>
                </div>
                {r.status==="open"
                  ?<button className="btn" onClick={()=>resolveReport(r.id)}>Resolve</button>
                  :<StatusChip s="Hired"/>}
              </div>
            ))}
          </section>
        )}

        {userRole==="admin" && tab==="stats" && (
          <section>
            <h2 className="sec-h">Platform Stats</h2>
            <div className="stat-grid">
              <StatBox n={jobs.filter(j=>j.status==="live").length} l="Live jobs"/>
              <StatBox n={jobs.filter(j=>j.status==="pending").length} l="Pending approval"/>
              <StatBox n={employers.filter(e=>e.status==="approved").length} l="Verified employers"/>
              <StatBox n={applications.length} l="Total applications"/>
              <StatBox n={applications.filter(a=>a.status==="Hired").length} l="Hires made"/>
              <StatBox n={reports.filter(r=>r.status==="open").length} l="Open scam reports"/>
            </div>

            <div className="csv-upload-box">
              <h3 className="sub-h" style={{marginTop:0}}>Bulk Upload Jobs via CSV</h3>
              <p className="muted" style={{marginBottom:12}}>Fill in the CSV template in Excel or Google Sheets, then upload here. All jobs are added as <strong>pending</strong> — go to Approvals to review before they go live.</p>
              <div className="csv-steps">
                <div className="csv-step"><span className="csv-num">1</span><span>Download the template below and open in Excel or Google Sheets</span></div>
                <div className="csv-step"><span className="csv-num">2</span><span>Fill in job details — one job per row</span></div>
                <div className="csv-step"><span className="csv-num">3</span><span>Save as CSV and upload here</span></div>
                <div className="csv-step"><span className="csv-num">4</span><span>Go to Approvals tab to review and approve jobs</span></div>
              </div>
              <div className="csv-cols">
                <strong>Required columns:</strong> title, employer_name, province, district, job_type, salary, description, requirements, closing_date, positions
              </div>
              <div className="btn-row" style={{marginTop:14}}>
                <a className="btn gold" href="data:text/csv;charset=utf-8,title%2Cemployer_name%2Cprovince%2Cdistrict%2Cjob_type%2Csalary%2Cdescription%2Crequirements%2Cclosing_date%2Cpositions%0AJob+Title+Here%2CCompany+Name%2CNational+Capital+District%2CWaigani%2CFull-time%2CK800-K1000%2FFornight%2CWrite+job+description+here%2CGrade+12+certificate%2C2026-08-31%2C1" download="png_jobs_template.csv">
                  ⬇ Download CSV Template
                </a>
                <label className={"btn"+(csvUploading?" ghost":"")}>
                  {csvUploading ? "Uploading…" : "⬆ Upload CSV File"}
                  <input type="file" accept=".csv" style={{display:"none"}} onChange={handleCSVUpload} disabled={csvUploading}/>
                </label>
              </div>
              {csvResults && (
                <div className={"csv-result "+(csvResults.failed>0?"has-errors":"")}>
                  <div>✓ {csvResults.success} jobs uploaded as pending</div>
                  {csvResults.failed>0 && <div>✗ {csvResults.failed} failed</div>}
                  {csvResults.errors.length>0 && <ul>{csvResults.errors.map((e,i)=><li key={i} className="muted small">{e}</li>)}</ul>}
                  {csvResults.success>0 && <div className="muted" style={{marginTop:6}}>Go to <strong>Approvals</strong> tab to review and publish jobs.</div>}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Job detail modal */}
      {activeJob&&(
        <div className="overlay" onClick={()=>{setActiveJob(null);setApplying(false);}}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <button className="close-x" onClick={()=>{setActiveJob(null);setApplying(false);}}>✕</button>
            <div className="j-title lg">{activeJob.title}</div>
            <div className="j-emp">{activeJob.employer_name}</div>
            <div className="j-meta" style={{marginTop:10}}>
              <Chip c="loc">{activeJob.district?activeJob.district+", ":""}{activeJob.province}</Chip>
              <Chip c="typ">{activeJob.job_type}</Chip>
              {activeJob.salary&&<Chip c="sal">{activeJob.salary}</Chip>}
              <Chip>Closes {activeJob.closing_date}</Chip>
            </div>
            {!applying?(
              <>
                <div className="sub-h">About this job</div>
                <p>{activeJob.description}</p>
                {(activeJob.requirements||[]).length>0&&<><div className="sub-h">Requirements</div><ul className="req-list">{(activeJob.requirements||[]).map((r,i)=><li key={i}>{r}</li>)}</ul></>}
                <div className="modal-foot">
                  {hasApplied(activeJob.id)
                    ?<span className="applied-lbl big">✓ You have applied — track it in My Applications</span>
                    :<button className="btn gold big" onClick={()=>startApply(activeJob)}>
                      {session?"Apply now":"Sign in to apply"}
                    </button>}
                  <button className="btn ghost" onClick={()=>reportJob(activeJob)}>Report this job</button>
                </div>
              </>
            ):(
              <>
                {(activeJob.screening_questions||[]).length>0&&(
                  <><div className="sub-h">Screening questions</div>
                  {(activeJob.screening_questions||[]).map((qs,i)=>(
                    <div key={i} className="scr-q">
                      <div className="muted" style={{fontWeight:600,marginBottom:8}}>{qs}</div>
                      <div className="btn-row">
                        {["Yes","No"].map(v=>(
                          <button key={v} className={"pill"+(answers[i]===v?" on":"")} onClick={()=>{const c=[...answers];c[i]=v;setAnswers(c);}}>{v}</button>
                        ))}
                      </div>
                    </div>
                  ))}</>
                )}
                <div className="sub-h">Cover letter</div>
                <textarea className="inp" rows={7} value={cover} onChange={e=>setCover(e.target.value)} placeholder="Write a short cover letter, or generate one from your CV…"/>
                <div className="btn-row" style={{marginTop:8}}>
                  <button className="btn" onClick={()=>genCover(activeJob)}>✨ Generate from my CV</button>
                </div>
                <div className="modal-foot">
                  <button className="btn gold big" onClick={()=>submitApp(activeJob)} disabled={loading}>{loading?"Submitting…":"Submit application"}</button>
                  <button className="btn ghost" onClick={()=>setApplying(false)}>Back</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Applicant detail modal */}
      {activeApp&&(
        <div className="overlay" onClick={()=>setActiveApp(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <button className="close-x" onClick={()=>setActiveApp(null)}>✕</button>
            <div className="j-title lg">{activeApp.applicant_name}</div>
            <div className="muted" style={{marginBottom:8}}>Applied {activeApp.created_at?.slice(0,10)}</div>
            {activeApp.cv_file_url && (
              <a href={activeApp.cv_file_url} target="_blank" rel="noopener noreferrer" className="btn gold sm" style={{marginBottom:12,display:"inline-block"}}>
                ⬇ Download CV ({activeApp.cv_file_name || "CV"})
              </a>
            )}
            <div className="pipeline" style={{marginBottom:16}}>
              <StatusChip s={activeApp.status}/>
              <select className="inp tiny" value={activeApp.status} onChange={e=>{updateAppStatus(activeApp.id,e.target.value);setActiveApp({...activeApp,status:e.target.value});}}>
                {STATUS_FLOW.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            {(activeApp.screening_answers||[]).length>0&&(
              <>
                <div className="sub-h">Screening answers</div>
                {(jobs.find(j=>j.id===activeApp.job_id)?.screening_questions||[]).map((q,i)=>(
                  <div key={i} className="scr-q" style={{marginBottom:8}}>
                    <div className="muted" style={{fontWeight:600,marginBottom:4}}>{q}</div>
                    <span className={"chip "+(activeApp.screening_answers[i]==="Yes"?"chip-typ":"chip-loc")}>{activeApp.screening_answers[i]||"—"}</span>
                  </div>
                ))}
              </>
            )}
            {activeApp.cover_letter&&(
              <>
                <div className="sub-h">Cover letter</div>
                <div style={{background:"var(--card)",border:"1.5px solid var(--line)",borderRadius:10,padding:"14px 16px",fontSize:13.5,whiteSpace:"pre-wrap",lineHeight:1.6}}>
                  {activeApp.cover_letter}
                </div>
              </>
            )}
            {!activeApp.cover_letter&&<p className="muted" style={{marginTop:8}}>No cover letter submitted.</p>}
            <div className="modal-foot">
              <button className="btn gold" onClick={()=>{updateAppStatus(activeApp.id,"Shortlisted");setActiveApp({...activeApp,status:"Shortlisted"});flash("Applicant shortlisted.");}}>Shortlist</button>
              <button className="btn" onClick={()=>{updateAppStatus(activeApp.id,"Interview");setActiveApp({...activeApp,status:"Interview"});flash("Interview requested.");}}>Request interview</button>
              <button className="btn ghost" onClick={()=>{updateAppStatus(activeApp.id,"Unsuccessful");setActiveApp(null);flash("Applicant marked unsuccessful.");}}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div className="toast">{toast}</div>}

      <footer className="foot">
        <div className="weave"/>
        <div className="foot-in"><strong>PNG Jobs Connect</strong> · Free for jobseekers, always · Never pay money to apply for a job</div>
      </footer>
    </div>
  );
}

function Chip({c,children}){return <span className={"chip"+(c?" chip-"+c:"")}>{children}</span>;}
function Label({children}){return <label className="lbl">{children}</label>;}
function StatusChip({s}){return <span className={"chip st st-"+s?.toLowerCase().replace(/\s/g,"-")}>{s}</span>;}
function StatBox({n,l}){return <div className="stat-box"><div className="stat-n">{n}</div><div className="stat-l">{l}</div></div>;}
function Accordion({title,body}){
  const [open,setOpen]=useState(false);
  return(
    <div className={"acc"+(open?" open":"")}>
      <button className="acc-h" onClick={()=>setOpen(!open)}>{title}<span>{open?"–":"+"}</span></button>
      {open&&<div className="acc-b">{body}</div>}
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..800&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
:root{--ink:#171310;--paper:#FBF8F2;--card:#FFFFFF;--red:#C8102E;--gold:#F0AC12;--goldsoft:#FCEBBE;--leaf:#1E6B52;--line:#E7DFCE;--mut:#6E655A;}
*{box-sizing:border-box;margin:0}
.root{font-family:'Public Sans',sans-serif;background:var(--paper);color:var(--ink);min-height:100vh;font-size:15px;line-height:1.55}
h1,h2,.logo-text,.j-title,.stat-n,.cv-name,.auth-logo{font-family:'Bricolage Grotesque',sans-serif}
.hdr{background:var(--ink);color:var(--paper)}
.hdr-in{max-width:1060px;margin:0 auto;padding:12px 18px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
.brand{display:flex;gap:11px;align-items:center}
.logo-text{font-size:20px;font-weight:750}.logo-text em{font-style:normal;color:var(--gold)}
.logo-tag{font-size:11px;color:#BDB2A2;letter-spacing:.3px}
.hdr-right{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.role-sw{display:flex;align-items:center;gap:4px;background:#241F19;padding:4px;border-radius:999px}
.role-btn{background:none;border:none;color:#CFC5B4;padding:5px 11px;border-radius:999px;cursor:pointer;font-family:'Public Sans',sans-serif;font-weight:600;font-size:13px}
.role-btn.on{background:var(--gold);color:var(--ink)}
.user-pill{display:flex;align-items:center;gap:8px}
.user-email{font-size:12px;color:#BDB2A2;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.weave{height:8px;background:repeating-linear-gradient(-45deg,var(--gold) 0 8px,var(--red) 8px 16px,var(--ink) 16px 24px)}
.scam-warn{background:var(--goldsoft);border-bottom:1px solid #EAD79A;padding:8px 18px;font-size:13px;text-align:center}
.tabs{background:#fff;border-bottom:1.5px solid var(--line);position:sticky;top:0;z-index:30}
.tabs-inner{max-width:1060px;margin:0 auto;padding:0 18px;display:flex;gap:2px;overflow-x:auto}
.tab-btn{background:none;border:none;border-bottom:3px solid transparent;padding:12px 14px;font-family:'Public Sans',sans-serif;font-weight:600;font-size:14px;color:var(--mut);cursor:pointer;white-space:nowrap;display:flex;align-items:center;gap:6px}
.tab-btn.on{color:var(--red);border-bottom-color:var(--red)}
.badge{background:var(--red);color:#fff;border-radius:999px;font-size:11px;padding:1px 7px;font-weight:700}
.main{max-width:1060px;margin:0 auto;padding:22px 18px 80px}
.hero{padding:10px 0 18px}
.hero h1{font-size:clamp(24px,4vw,36px);font-weight:770;line-height:1.12;letter-spacing:-.3px}
.hero h1 span{color:var(--red)}.hero p{color:var(--mut);margin-top:6px}
.sec-h{font-size:21px;font-weight:730;margin-bottom:14px}.sec-h.gap{margin-top:28px}
.filter-row{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px}
.f-inp{border:1.5px solid var(--line);border-radius:9px;padding:9px 12px;font-family:'Public Sans',sans-serif;font-size:14px;background:var(--card);color:var(--ink)}
.f-inp.grow{flex:1 1 200px}
.inp{border:1.5px solid var(--line);border-radius:9px;padding:9px 12px;font-family:'Public Sans',sans-serif;font-size:14px;background:var(--card);color:var(--ink);width:100%}
.inp:focus{outline:2px solid var(--gold);outline-offset:1px}
.inp.tiny{width:auto;padding:5px 8px;font-size:12px}
textarea.inp{resize:vertical}
.lbl{display:block;font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;color:var(--mut);margin:12px 0 5px}
.sub-h{font-weight:800;font-size:12px;text-transform:uppercase;letter-spacing:1px;color:var(--red);margin:18px 0 8px}
.row2{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.row3{display:grid;grid-template-columns:2fr 2fr 1fr;gap:8px;margin-bottom:8px}
.exp-block{margin-bottom:10px}
@media(max-width:600px){.row2,.row3{grid-template-columns:1fr}}
.job-grid{display:grid;gap:12px}
.jcard{background:var(--card);border:1.5px solid var(--line);border-radius:13px;padding:16px 18px;cursor:pointer;transition:transform .12s,border-color .12s}
.jcard:hover{transform:translateY(-2px);border-color:var(--gold)}
.jcard.flat{cursor:default}.jcard.flat:hover{transform:none;border-color:var(--line)}
.j-title{font-size:17px;font-weight:700;font-family:'Bricolage Grotesque',sans-serif;margin-bottom:2px}
.j-title.sm{font-size:15px}.j-title.lg{font-size:22px;margin-top:4px}
.j-emp{color:var(--mut);font-size:13px;margin-bottom:8px}
.j-meta{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:8px}
.j-foot{display:flex;justify-content:space-between;align-items:center;font-size:13px;color:var(--mut);flex-wrap:wrap;gap:6px}
.view-lnk{color:var(--red);font-weight:700}.applied-lbl{color:var(--leaf);font-weight:700}.applied-lbl.big{font-size:14px}
.chip{font-size:12px;font-weight:600;padding:3px 10px;border-radius:999px;background:#F1EBDD;color:#5B5347}
.chip-loc{background:#EDE4F0;color:#5B3A6E}.chip-typ{background:#E2EFE9;color:var(--leaf)}.chip-sal{background:var(--goldsoft);color:#7A5A00}
.st{display:inline-block}
.st-submitted{background:#F1EBDD;color:#5B5347}.st-viewed{background:#E4ECF5;color:#2C4A6E}
.st-shortlisted{background:var(--goldsoft);color:#7A5A00}.st-interview{background:#EDE4F0;color:#5B3A6E}
.st-hired,.st-live{background:#DDEFE6;color:var(--leaf)}.st-unsuccessful,.st-rejected{background:#F5E0E0;color:var(--red)}
.st-pending{background:#FBEED2;color:#8A6100}.st-closed{background:#EEE;color:#888}
.row-card{background:var(--card);border:1.5px solid var(--line);border-radius:12px;padding:13px 16px;display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:9px;flex-wrap:wrap}
.row-card.dim{opacity:.5}
.app-list{border-top:1.5px dashed var(--line);margin-top:12px;padding-top:12px}
.app-row{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--line);flex-wrap:wrap}
.app-row:last-child{border-bottom:none}
.pipeline{display:flex;gap:8px;align-items:center}
.btn{border:1.5px solid var(--ink);background:var(--ink);color:var(--paper);padding:8px 16px;border-radius:9px;cursor:pointer;font-family:'Public Sans',sans-serif;font-weight:700;font-size:13.5px}
.btn.gold{background:var(--gold);border-color:var(--gold);color:var(--ink)}
.btn.ghost{background:none;color:var(--ink);border-color:var(--line)}
.btn.big{padding:11px 22px;font-size:15px}.btn.sm{padding:5px 12px;font-size:12px}
.btn:disabled{opacity:.5;cursor:not-allowed}
.btn-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
.add-btn{background:none;border:1.5px dashed var(--line);border-radius:9px;padding:7px 13px;cursor:pointer;color:var(--mut);font-family:'Public Sans',sans-serif;font-weight:600;font-size:13px;margin-top:8px}
.pill{border:1.5px solid var(--line);background:var(--card);border-radius:999px;padding:6px 18px;cursor:pointer;font-weight:700;font-family:'Public Sans',sans-serif;font-size:13px}
.pill.on{background:var(--ink);color:var(--paper);border-color:var(--ink)}
.save-row{display:flex;gap:10px;margin-top:20px;flex-wrap:wrap}
.post-wrap{max-width:660px}
.cv-wrap{display:grid;grid-template-columns:1fr 1fr;gap:22px;align-items:start}
@media(max-width:860px){.cv-wrap{grid-template-columns:1fr}}
.cv-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap;margin-bottom:8px}
.cv-head-right{display:flex;flex-direction:column;align-items:flex-end;gap:8px}
.score-bar-wrap{display:flex;align-items:center;gap:8px}
.score-bar{width:100px;height:8px;background:#EDE6D6;border-radius:99px;overflow:hidden}
.score-bar div{height:100%;background:linear-gradient(90deg,var(--gold),var(--red));border-radius:99px;transition:width .3s}
.cv-preview{position:sticky;top:80px}
.cv-paper{background:#fff;border:1.5px solid var(--line);border-radius:4px;padding:26px 28px;box-shadow:0 3px 14px rgba(23,19,16,.07);margin-bottom:12px;min-height:280px}
.cv-name{font-size:22px;font-weight:770}.cv-contact{color:var(--mut);font-size:12.5px;border-bottom:2px solid var(--ink);padding-bottom:8px;margin-bottom:4px}
.cv-sec{font-weight:800;font-size:11px;letter-spacing:1.2px;text-transform:uppercase;color:var(--red);margin:13px 0 3px}
.cv-paper p{font-size:13.5px;margin:3px 0}
.stat-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:12px;margin-bottom:16px}
.stat-box{background:var(--card);border:1.5px solid var(--line);border-radius:13px;padding:18px}
.stat-n{font-size:32px;font-weight:770;color:var(--red);font-family:'Bricolage Grotesque',sans-serif}
.stat-l{font-size:12px;color:var(--mut);text-transform:uppercase;letter-spacing:.5px;margin-top:2px}
.acc{background:var(--card);border:1.5px solid var(--line);border-radius:12px;margin-bottom:9px;overflow:hidden}
.acc-h{width:100%;display:flex;justify-content:space-between;background:none;border:none;padding:14px 17px;cursor:pointer;font-family:'Bricolage Grotesque',sans-serif;font-weight:700;font-size:15px;color:var(--ink)}
.acc.open .acc-h{border-bottom:1.5px dashed var(--line)}.acc-b{padding:13px 17px;color:#463F35;font-size:14px}
.overlay{position:fixed;inset:0;background:rgba(23,19,16,.55);display:flex;justify-content:center;align-items:flex-start;padding:26px 14px;z-index:50;overflow-y:auto}
.modal{background:var(--paper);border-radius:16px;max-width:620px;width:100%;padding:24px 26px 26px;position:relative;border-top:5px solid var(--gold)}
.auth-modal{max-width:420px}
.auth-logo{font-size:24px;font-weight:770;margin-bottom:4px}.auth-logo em{font-style:normal;color:var(--gold)}
.auth-tabs{display:flex;gap:0;border:1.5px solid var(--line);border-radius:10px;overflow:hidden;margin-bottom:4px}
.auth-tab{flex:1;border:none;background:none;padding:9px;font-family:'Public Sans',sans-serif;font-weight:600;font-size:14px;cursor:pointer;color:var(--mut)}
.auth-tab.on{background:var(--ink);color:var(--paper)}
.auth-err{background:#FEE;border:1.5px solid #FCC;border-radius:9px;padding:10px 14px;color:var(--red);font-size:13.5px;margin-top:10px}
.auth-prompt{background:var(--card);border:1.5px dashed var(--line);border-radius:12px;padding:24px;text-align:center}
.auth-prompt p{margin-bottom:12px;color:var(--mut)}
.link-btn{background:none;border:none;color:var(--red);font-weight:700;cursor:pointer;font-family:'Public Sans',sans-serif;font-size:14px;text-decoration:underline}
.close-x{position:absolute;top:12px;right:14px;border:none;background:#EDE6D6;border-radius:99px;width:30px;height:30px;cursor:pointer;font-size:13px}
.req-list{padding-left:20px}.req-list li{margin:3px 0;font-size:14px}
.modal-foot{display:flex;gap:10px;align-items:center;margin-top:20px;flex-wrap:wrap}
.scr-q{background:var(--card);border:1.5px solid var(--line);border-radius:11px;padding:11px 14px;margin-bottom:9px}
.loading{text-align:center;color:var(--mut);padding:30px;font-size:14px}
.empty{background:var(--card);border:1.5px dashed var(--line);border-radius:12px;padding:26px;text-align:center;color:var(--mut);font-size:14px}
.empty.sm{padding:14px;margin-bottom:10px}
.muted{color:var(--mut);font-size:13px}.note{margin:6px 0 14px}.small{font-size:12px}.dim{opacity:.5}
.toast{position:fixed;bottom:22px;left:50%;transform:translateX(-50%);background:var(--ink);color:var(--paper);padding:11px 20px;border-radius:11px;font-weight:600;font-size:14px;z-index:60;box-shadow:0 6px 22px rgba(0,0,0,.25);border-left:5px solid var(--gold);max-width:92vw}
.foot{margin-top:30px}.foot-in{background:var(--ink);color:#BDB2A2;text-align:center;padding:16px;font-size:13px}.foot-in strong{color:var(--gold)}

.notif-wrap{max-width:1060px;margin:0 auto;padding:10px 18px 0}
.notif-bar{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:11px 16px;border-radius:11px;margin-bottom:8px;font-size:14px;font-weight:600}
.notif-shortlisted{background:#FCEBBE;border:1.5px solid #E6C868;color:#7A5A00}
.notif-interview{background:#EDE4F0;border:1.5px solid #C8A8E0;color:#5B3A6E}
.notif-hired{background:#DDEFE6;border:1.5px solid #8FD3B0;color:var(--leaf)}
.notif-unsuccessful{background:#F5E0E0;border:1.5px solid #E8A8A8;color:var(--red)}
.notif-close{background:none;border:none;cursor:pointer;font-size:14px;opacity:.6;padding:0 4px}
.csv-upload-box{background:var(--card);border:1.5px solid var(--line);border-radius:14px;padding:22px 24px;margin-top:20px}
.csv-steps{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
.csv-step{display:flex;align-items:flex-start;gap:10px;font-size:14px}
.csv-num{background:var(--red);color:#fff;border-radius:999px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex:none}
.csv-cols{background:#F7F3EA;border-radius:9px;padding:10px 14px;font-size:12.5px;color:var(--mut);border:1px solid var(--line)}
.csv-result{background:#DDEFE6;border:1.5px solid #8FD3B0;border-radius:10px;padding:12px 16px;margin-top:14px;font-size:14px;font-weight:600;color:var(--leaf)}
.csv-result.has-errors{background:#FEE;border-color:#FCC;color:var(--red)}
.csv-result ul{margin-top:6px;padding-left:18px}
.cv-upload-card{background:linear-gradient(135deg,#FBF3DC,#FDF8F0);border:1.5px solid #E6C868;border-radius:12px;padding:16px 18px;margin-bottom:16px}
.cv-upload-title{font-weight:700;font-size:15px;margin-bottom:4px;font-family:'Bricolage Grotesque',sans-serif}
.cv-file-saved{display:flex;align-items:center;gap:10px;background:var(--card);border:1.5px solid var(--line);border-radius:9px;padding:8px 12px;font-size:13px;font-weight:600;margin-bottom:4px}
a.btn{display:inline-block;text-decoration:none;text-align:center}
@media print{.hdr,.tabs,.scam-warn,.filter-row,.foot,.toast,.btn,.role-sw,.hdr-right{display:none!important}.cv-wrap{grid-template-columns:1fr}.cv-paper{border:none;box-shadow:none}}
`;
